import { type CSSProperties } from 'react'
import { DashboardUserMenu } from '@/widgets/dashboard-user-menu'
import { PaymentHistoryPanel, type PaymentHistoryItem } from '@/widgets/payment-history'
import { Button, ContentGroup, InputField } from '@/shared/ui'
import { designTokens } from '@/shared/lib/design-tokens'
import { typographyStyle } from '@/shared/lib/catalog'
import { cn } from '@/shared/lib'
import { env } from '@/shared/config'

export type DashboardSubscriptionUiStatus =
  | 'active'
  | 'pending_cancellation'
  | 'on_hold'
  | 'not_active'

export type DashboardSubscriptionShellVariant = 'dashboard' | 'catalog'

type SubscriptionVisualTone = 'default' | 'catalog-dark'

type DashboardSubscriptionDesktopProps = {
  className?: string
  shellVariant?: DashboardSubscriptionShellVariant
  status?: DashboardSubscriptionUiStatus
  planTitle: string
  renewalText: string | null
  promoBadgeText?: string | null
  paymentHistoryItems: PaymentHistoryItem[]
}

const desktopSectionGap = designTokens.components.account.layout.sectionGap

const searchInputContainerStyle = {
  maxWidth: designTokens.components.account.search.width,
}

const searchInputStyle = {
  height: designTokens.components.account.search.height,
  backgroundColor: designTokens.colors.background.glass,
  borderRadius: designTokens.borders.radius.sm,
  border: `${designTokens.borders.widths.thin} ${designTokens.borders.styles.solid} ${designTokens.colors.border.adminPattern}`,
  paddingRight: designTokens.components.account.search.paddingRight,
  fontFamily: designTokens.typography.fontFamilies.primary,
  ...typographyStyle(designTokens.typography.body.base),
  color: designTokens.colors.text.primary,
}

const subscriptionTitleStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.neutral.ink,
  ...typographyStyle(designTokens.typography.account.advertisingTitle),
}

const subscriptionBodyStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.strong,
  ...typographyStyle(designTokens.typography.body.lg),
}

const statusBadgeBaseStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: designTokens.spacing.xs,
  padding: `${designTokens.spacing.xxs} ${designTokens.spacing.xs}`,
  borderRadius: designTokens.borders.radius.pill,
}

const statusLabelStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  ...typographyStyle(designTokens.typography.subscription.statusLabel),
}

const catalogDarkPanelStyle: CSSProperties = {
  background: designTokens.colors.background.catalogSubscriptionPanel,
  borderColor: designTokens.colors.border.catalogSubscriptionPanel,
  backdropFilter: 'none',
  WebkitBackdropFilter: 'none',
}

const resolveTone = (shellVariant: DashboardSubscriptionShellVariant): SubscriptionVisualTone =>
  shellVariant === 'catalog' ? 'catalog-dark' : 'default'

const toneTextStyle = <T extends CSSProperties>(style: T, tone: SubscriptionVisualTone): T =>
  tone === 'catalog-dark' ? ({ ...style, color: '#FEFEFE' } as T) : style

const tonePanelStyle = (tone: SubscriptionVisualTone) =>
  tone === 'catalog-dark' ? catalogDarkPanelStyle : undefined

const SubscriptionStatusBadge = ({
  status,
  promoText,
}: {
  status: 'active' | 'pending_cancellation' | 'on_hold'
  promoText?: string | null
}) => {
  const activeStyles =
    status === 'active' || status === 'pending_cancellation'
      ? {
          backgroundColor: designTokens.colors.border.successStrong,
          color: designTokens.colors.text.inverse,
        }
      : {
          backgroundColor: designTokens.colors.border.light,
          color: designTokens.colors.text.primary,
        }

  return (
    <span style={{ ...statusBadgeBaseStyle, ...activeStyles }}>
      <span style={{ ...statusLabelStyle, color: activeStyles.color }}>
        {status === 'on_hold' ? 'Приостановлена' : 'активна'}
      </span>
      {promoText ? (
        <span style={{ ...statusLabelStyle, color: activeStyles.color }}>{promoText}</span>
      ) : null}
    </span>
  )
}

const SubscriptionInfoPanel = ({
  status,
  planTitle,
  renewalText,
  promoBadgeText,
  tone,
}: {
  status: 'active' | 'pending_cancellation' | 'on_hold'
  planTitle: string
  renewalText: string | null
  promoBadgeText?: string | null
  tone: SubscriptionVisualTone
}) => (
  <ContentGroup
    variant="transparentPanel"
    justify="start"
    className="flex-1"
    style={{ gap: designTokens.spacing.xxl, ...tonePanelStyle(tone) }}
  >
    <div className="flex items-start" style={{ gap: designTokens.spacing.lg }}>
      <p style={toneTextStyle(subscriptionTitleStyle, tone)}>{planTitle}</p>
      <SubscriptionStatusBadge
        status={status}
        promoText={status === 'on_hold' ? null : promoBadgeText}
      />
    </div>
    {renewalText ? <p style={toneTextStyle(subscriptionBodyStyle, tone)}>{renewalText}</p> : null}
  </ContentGroup>
)

const SubscriptionEmptyPanel = ({ tone }: { tone: SubscriptionVisualTone }) => (
  <ContentGroup
    variant="transparentPanel"
    justify="start"
    className="w-full"
    style={tonePanelStyle(tone)}
  >
    <div className="flex w-full flex-col" style={{ gap: designTokens.spacing.xxs }}>
      <div
        className="flex w-full items-center justify-between"
        style={{ gap: designTokens.spacing.lg }}
      >
        <p style={toneTextStyle(subscriptionTitleStyle, tone)}>Активной подписки нет</p>
        <Button
          asChild
          variant="secondarySubmitMobile"
          style={{
            minHeight: designTokens.components.account.hero.buttonHeight,
            whiteSpace: 'nowrap',
            ...(tone === 'catalog-dark' ? { background: '#FEFEFE', color: '#222222' } : null),
          }}
        >
          <a href={env.academyPricingUrl}>Открыть Академию</a>
        </Button>
      </div>
      <p style={toneTextStyle(subscriptionBodyStyle, tone)}>
        Материалы Epic Growth теперь доступны в Академии
      </p>
    </div>
  </ContentGroup>
)

export function DashboardSubscriptionDesktop({
  className,
  shellVariant = 'dashboard',
  status = 'active',
  planTitle,
  renewalText,
  promoBadgeText,
  paymentHistoryItems,
}: DashboardSubscriptionDesktopProps) {
  const tone = resolveTone(shellVariant)
  const headerGap =
    shellVariant === 'catalog'
      ? designTokens.spacing.xxxl
      : designTokens.spacing.subscriptionHeaderGap

  return (
    <div
      className={cn('hidden w-full flex-col lg:flex', className)}
      style={{ gap: desktopSectionGap }}
    >
      {shellVariant === 'dashboard' ? (
        <div
          className="flex w-full items-center justify-between"
          style={{ gap: designTokens.spacing.xxl }}
        >
          <InputField
            type="search"
            placeholder="Поиск"
            aria-label="Поиск"
            variant="search"
            containerClassName="w-full"
            containerStyle={searchInputContainerStyle}
            inputClassName="w-full bg-transparent outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
            style={searchInputStyle}
          />
          <DashboardUserMenu />
        </div>
      ) : null}

      {status === 'not_active' ? (
        <div className="flex w-full items-stretch" style={{ gap: headerGap }}>
          <SubscriptionEmptyPanel tone={tone} />
        </div>
      ) : (
        <div className="flex w-full items-stretch" style={{ gap: headerGap }}>
          <SubscriptionInfoPanel
            status={status}
            planTitle={planTitle}
            renewalText={renewalText}
            promoBadgeText={promoBadgeText}
            tone={tone}
          />
        </div>
      )}
      {paymentHistoryItems.length > 0 ? (
        <PaymentHistoryPanel
          items={paymentHistoryItems}
          tone={tone}
          initialVisibleItems={shellVariant === 'catalog' ? 6 : undefined}
        />
      ) : null}
    </div>
  )
}

DashboardSubscriptionDesktop.displayName = 'DashboardSubscriptionDesktop'
